<template>
  <v-dialog v-model="isOpen" class="copy-modal-dialog">
    <v-card>
      <v-card-title class="copy-modal-title">
        <h2 class="copy-modal-title-text">
          {{ driveLink ? "Success!" : "Add Resource to Google Drive" }}
        </h2>
        <v-icon @click="handleClose">mdi-close</v-icon>
      </v-card-title>
      <div class="modal-divider-wrapper">
        <span class="modal-divider"></span>
      </div>

      <v-card-text>
        <div class="copy-modal-content">
          <div class="copy-modal-content-center">
            <img src="/images/google-drive.png" alt="" />
            <h1 class="copy-modal-heading">
              {{
                driveLink
                  ? "Successfully added"
                  : "Copy this resource to your Google Drive"
              }}
            </h1>
            <p class="copy-modal-description">
              {{
                driveLink
                  ? "We've added a copy of the selected resource to your Google Drive"
                  : "Allow Eddy to add this resource to your Google Drive. This permissions allows us to make a copy of this resource within your Google Drive."
              }}
              <br />
              <small>Google Drive is a trademark of Google Inc.</small>
            </p>
          </div>

          <div class="copy-modal-buttons">
            <button class="btn-cancel" @click="handleClose">
              {{ driveLink ? "Back to Lesson Plan" : "Cancel" }}
            </button>
            <button
              class="btn-success"
              @click="handleCopy"
              :disabled="isLoading"
            >
              <template v-if="isLoading">
                <v-progress-circular
                  indeterminate
                  color="white"
                  size="20"
                ></v-progress-circular>
              </template>
              <template v-else>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  v-if="driveLink"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M2 5.31818C2 4.03741 3.03803 3 4.31818 3H6.34636C6.62251 3 6.84636 3.22386 6.84636 3.5C6.84636 3.77614 6.62251 4 6.34636 4H4.31818C3.59015 4 3 4.58986 3 5.31818V11.6818C3 12.4101 3.59015 13 4.31818 13H10.6818C11.4098 13 12 12.4101 12 11.6818V9.65455C12 9.3784 12.2239 9.15455 12.5 9.15455C12.7761 9.15455 13 9.3784 13 9.65455V11.6818C13 12.9626 11.962 14 10.6818 14H4.31818C3.03803 14 2 12.9626 2 11.6818V5.31818Z"
                    fill="white"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M9 2.5C9 2.22386 9.22386 2 9.5 2H13.5C13.7761 2 14 2.22386 14 2.5V6.5C14 6.77614 13.7761 7 13.5 7C13.2239 7 13 6.77614 13 6.5V3H9.5C9.22386 3 9 2.77614 9 2.5Z"
                    fill="white"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M13.4891 2.5269C13.6841 2.72248 13.6836 3.03906 13.488 3.23401L8.85298 7.85401C8.6574 8.04895 8.34082 8.04844 8.14587 7.85286C7.95093 7.65728 7.95144 7.3407 8.14702 7.14575L12.782 2.52575C12.9776 2.33081 13.2942 2.33132 13.4891 2.5269Z"
                    fill="white"
                  />
                </svg>
                {{ driveLink ? "View" : "Continue" }}
              </template>
            </button>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  driveLink: {
    type: String,
    default: "",
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "copy", "close"]);

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const handleClose = () => {
  isOpen.value = false;
  emit("close");
};

const handleCopy = () => {
  emit("copy");
};
</script>

<style scoped>
.copy-modal-dialog {
  width: 850px;
}

.copy-modal-title {
  display: flex;
  justify-content: space-between;
  padding: 20px;
}

.copy-modal-title-text {
  color: var(--primary-blue);
  font-size: var(--fs-xl);
  font-weight: 600;
}

.modal-divider-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
}

.modal-divider {
  width: 94%;
  border: solid 1px #f2f3fc;
}

.copy-modal-content {
  margin-block: 16px;
}

.copy-modal-content-center {
  width: 100%;
  text-align: center;
}

.copy-modal-heading {
  color: #00925e;
  font-size: 40px;
  font-weight: 600;
}

.copy-modal-description {
  font-size: var(--fs-medium);
  font-weight: 400;
  color: #384648;
  max-width: 80%;
  margin-inline: auto;
}

.copy-modal-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 12px;
}

.btn-cancel {
  color: #e00069;
  background-color: #fff;
  font-size: var(--fs-big);
  padding-block: 12px;
  padding-inline: 20px;
  border-radius: 16px;
  font-weight: 600;
  border: 1px solid #e00069;
  cursor: pointer;
}

.btn-success {
  color: #fff;
  background-color: #00925e;
  font-size: var(--fs-big);
  padding-block: 12px;
  padding-inline: 20px;
  border-radius: 16px;
  font-weight: 600;
  border: 1px solid #00925e;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
}

.btn-success:disabled {
  background-color: #999;
  border: #999;
}
</style>
